
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { trainingJournalAPI } from '../api';
import { toast } from '../components/common/Notifier';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { DailyTable } from '../components/journal/DailyTable';
import { PeriodTable} from "../components/journal/PeriodTable";
import { addWeeks, subWeeks, addDays } from 'date-fns';
import { CompareWorkoutsModal } from "../components/journal/CompareWorkoutsModal";


export const TrainingJournalPage = () => {
  const { t } = useTranslation();
  const { athlete } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workouts, setWorkouts] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [selectedDates, setSelectedDates] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  useEffect(() => {
    if (athlete) {
      // Устанавливаем текущий месяц при первой загрузке
      setCurrentMonth(new Date());
      setFromDate(startOfMonth(new Date()));
      setToDate(endOfMonth(new Date()));
    }
  }, [athlete]);

  useEffect(() => {
    setSelectedDates([]);
  }, [fromDate, toDate, filterType, period, favoritesOnly]);

  useEffect(() => {
    if (athlete) {
      loadWorkouts();
    }
  }, [athlete, fromDate, toDate, filterType, period, favoritesOnly]);

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
  const now = new Date();
  const day = now.getDay(); // 0=вс, 1=пн...
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // понедельник
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
  });
  const goToPreviousWeek = () => {
  const newStart = subWeeks(currentWeekStart, 1);
  setCurrentWeekStart(newStart);
  setFromDate(newStart);
  setToDate(addDays(newStart, 6));
  };

  const goToNextWeek = () => {
    const newStart = addWeeks(currentWeekStart, 1);
    setCurrentWeekStart(newStart);
    setFromDate(newStart);
    setToDate(addDays(newStart, 6));
  };

  const goToCurrentWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
    setFromDate(monday);
    setToDate(addDays(monday, 6));
  };

  const toggleCompareSelection = (date) => {
    setSelectedDates((prev) => {
      if (prev.includes(date)) {
        return prev.filter((d) => d !== date);
      }
      if (prev.length >= 3) {
        toast.warning(t('journal.compare_max_reached'));
        return prev;
      }
      return [...prev, date];
    });
  };

  const loadWorkouts = async () => {
    setLoading(true);
    try {
      const response = await trainingJournalAPI.get({
        athlete_id: athlete.id,
        date_from: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
        date_to: toDate ? format(toDate, 'yyyy-MM-dd') : undefined,
        workout_type: filterType !== 'all' ? filterType : undefined,
        period: period,
        is_favorite: favoritesOnly ? true : undefined,
      });
      setWorkouts(response.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  // Навигация по месяцам
  const goToPreviousMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setFromDate(startOfMonth(newMonth));
    setToDate(endOfMonth(newMonth));
  };

  const goToNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setFromDate(startOfMonth(newMonth));
    setToDate(endOfMonth(newMonth));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setCurrentMonth(now);
    setFromDate(startOfMonth(now));
    setToDate(endOfMonth(now));
  };

  const handleDateChange = (field, value) => {
    if (field === 'from') {
      setFromDate(value ? new Date(value) : null);
    } else {
      setToDate(value ? new Date(value) : null);
    }
  };

  const workoutTypes = [
    { value: 'all', label: t('journal.filter_all_types') },
    { value: 'easy', label: t('workoutType.easy') },
    { value: 'threshold', label: t('workoutType.threshold') },
    { value: 'medium_intervals', label: t('workoutType.medium_intervals') },
    { value: 'short_intervals', label: t('workoutType.short_intervals') },
    { value: 'long_intervals', label: t('workoutType.long_intervals') },
    { value: 'short_reps', label: t('workoutType.short_reps') },
    { value: 'long_run', label: t('workoutType.long_run') },

    { value: 'prerace_intervals', label: t('workoutType.prerace_intervals') },

    { value: 'race', label: t('workoutType.race') },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">📓 {t('journal.title')}</h1>
      </div>


      <div className="bg-white rounded-xl shadow p-4 space-y-4">
        {/* Первая строка: период, навигация по неделям, навигация по месяцам, даты */}

        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Период */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">{t('journal.period')}</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="daily">{t('journal.period_daily')}</option>
              <option value="weekly">{t('journal.period_weekly')}</option>
              <option value="monthly">{t('journal.period_monthly')}</option>
            </select>
          </div>

          {/* Навигация по неделям */}
          <div className="flex items-center gap-2">
            <button onClick={goToPreviousWeek} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">‹</button>
            <button onClick={goToCurrentWeek} className="px-3 py-2 bg-green-100 text-gray-600 rounded-lg hover:bg-green-700 text-sm font-medium whitespace-nowrap">
              {t('journal.this_week')}
            </button>
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {format(currentWeekStart, 'dd.MM')} - {format(addDays(currentWeekStart, 6), 'dd.MM')}
            </span>
            <button onClick={goToNextWeek} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">›</button>
          </div>

          {/* Навигация по месяцам */}
          <div className="flex items-center gap-2">
            <button onClick={goToPreviousMonth} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">‹</button>
            <button onClick={goToCurrentMonth} className="px-3 py-2 bg-blue-100 text-gray-600 rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap">
              {t('journal.this_month')}
            </button>
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button onClick={goToNextMonth} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">›</button>
          </div>

          {/* Дата от/до — ml-auto убран */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <label className="text-sm text-gray-600">{t('journal.from')}</label>
              <input
                type="date"
                value={fromDate ? format(fromDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleDateChange('from', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-sm text-gray-600">{t('journal.to')}</label>
              <input
                type="date"
                value={toDate ? format(toDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleDateChange('to', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Вторая строка: быстрые кнопки-фильтры по типам + выпадающий список */}
        <div className="flex flex-wrap items-center gap-3 border-t pt-3">
          <span className="text-sm font-medium text-gray-600">{t('journal.filter_type')}:</span>
          {/* Только избранные */}
          <button
            onClick={() => setFavoritesOnly((prev) => !prev)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
              favoritesOnly
                ? 'bg-red-600 text-white'
                : 'bg-red-50 text-red-600 hover:bg-red-100'
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-3.5 h-3.5"
              fill={favoritesOnly ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
            {t('journal.favorites_only')}
          </button>
          {/* Кнопка "Все" */}
          <button
            onClick={() => {
              setFilterType('all');
              // loadWorkouts() вызовется автоматически через useEffect
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterType === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Все
          </button>

          {/* Easy run */}
          <button
            onClick={() => setFilterType('easy')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterType === 'easy'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            🟢 Easy
          </button>

          {/* Threshold */}
          <button
            onClick={() => setFilterType('threshold')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterType === 'threshold'
                ? 'bg-orange-600 text-white'
                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
            }`}
          >
            🟠 Threshold
          </button>

          {/* Long Run */}
          <button
            onClick={() => setFilterType('long_run')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterType === 'long_run'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            🔵 Long Run
          </button>

          {/* Race */}
          <button
            onClick={() => setFilterType('race')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterType === 'race'
                ? 'bg-red-600 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            🔴 Race
          </button>

          {/* Выпадающий список для остальных типов (опционально) */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ml-auto"
          >
            {workoutTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

    {/* Таблица */}
    {workouts.length === 0 ? (
      <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
        {t('journal.no_workouts')}
      </div>
    ) : period === 'daily' ? (
      <DailyTable
        rows={workouts}
        t={t}
        selectedDates={selectedDates}
        onToggleSelect={toggleCompareSelection}
      />
    ) : (
      <PeriodTable
        rows={workouts}
        periodKey={period === 'weekly' ? 'week_start' : 'month_start'}
        period={period}
        t={t}
      />
    )}
    {selectedDates.length >= 2 && (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-full px-5 py-3 shadow-lg flex items-center gap-3 z-40">
        <span className="text-sm">{t('journal.selected_count', { count: selectedDates.length })}</span>
        <button
          onClick={() => setCompareModalOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 px-4 py-1.5 rounded-full text-sm font-medium"
        >
          {t('journal.compare_now')}
        </button>
        <button onClick={() => setSelectedDates([])} className="text-gray-400 hover:text-white text-sm">✕</button>
      </div>
    )}

    {compareModalOpen && (
      <CompareWorkoutsModal
        dates={selectedDates}
        onClose={() => setCompareModalOpen(false)}
      />
    )}



  </div>
);
};



